import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Stack, Typography, Autocomplete, Button, Divider, Grid } from '@mui/material';
import api from '../services/api';
import TextField from '../components/common/TextField';

export const PricesPage: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);

  // Inputs por canal
  const [pvp, setPvp] = useState<number>(0);
  const [pvpSim, setPvpSim] = useState<number>(0);
  const [horeca, setHoreca] = useState<number>(0);
  const [comercios, setComercios] = useState<number>(0);
  const [distribuidores, setDistribuidores] = useState<number>(0);
  const [mlCommissionPct, setMlCommissionPct] = useState<number>(0);
  const [mlFreeShipping, setMlFreeShipping] = useState<boolean>(false);
  const [mlShippingCost, setMlShippingCost] = useState<number>(0);
  const [mlUnitsPerSale, setMlUnitsPerSale] = useState<number>(1);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.getProducts({ page: 1, pageSize: 500 });
        setProducts(p.products || []);
      } catch {}
      try {
        const m = await api.getMaterials({ page: 1, pageSize: 1000 });
        setMaterials(m.materials || []);
      } catch {}
      try {
        const s = await api.getServices({ page: 1, pageSize: 1000 });
        setServices(s.services || []);
      } catch {}
    })();
  }, []);

  const materialsMap = useMemo(() => new Map(materials.map((m: any) => [m.id, m])), [materials]);
  const servicesMap = useMemo(() => new Map(services.map((s: any) => [s.id, s])), [services]);

  const [recipe, setRecipe] = useState<any | null>(null);
  const [qty, setQty] = useState<number>(1000);
  const [costResp, setCostResp] = useState<any | null>(null);
  useEffect(() => {
    (async () => {
      if (!selected) { setRecipe(null); return; }
      try {
        const res = await api.getRecipes({ page: 1, pageSize: 1, productId: selected.id });
        setRecipe(res.recipes?.[0] || null);
      } catch { setRecipe(null); }
    })();
  }, [selected]);

  useEffect(() => {
    (async () => {
      if (!recipe) { setCostResp(null); return; }
      try {
        const c = await api.getRecipeCost(recipe.id, Math.max(1, qty));
        setCostResp(c);
      } catch { setCostResp(null); }
    })();
  }, [recipe, qty]);

  const cost = useMemo(() => {
    if (costResp) return Number(costResp.unitCostTotal || 0);
    // fallback rápido si no hay endpoint
    if (!recipe) return 0;
    let c = 0;
    for (const it of (recipe.items || [])) {
      if (it.componentMaterialId && materialsMap.get(it.componentMaterialId)) {
        const m = materialsMap.get(it.componentMaterialId);
        c += (Number(it.quantity) || 0) * (Number(m.cost) || 0);
      } else if ((it as any).componentServiceId && servicesMap.get((it as any).componentServiceId)) {
        const s = servicesMap.get((it as any).componentServiceId);
        c += (Number(it.quantity) || 0) * (Number(s.cost) || 0);
      }
    }
    return Number.isFinite(c) ? Number(c.toFixed(2)) : 0;
  }, [costResp, recipe, materialsMap, servicesMap]);

  const serviceHours = useMemo(() => {
    if (!recipe) return 0;
    let h = 0;
    for (const it of (recipe.items || [])) {
      const svcId = (it as any).componentServiceId;
      if (svcId && servicesMap.get(svcId)) {
        const s = servicesMap.get(svcId);
        h += (Number(it.quantity) || 0) * (Number(s.processingHoursPerUnit) || 0);
      }
    }
    return Number.isFinite(h) ? Number(h.toFixed(2)) : 0;
  }, [recipe, servicesMap]);

  const margin = (price: number) => price > 0 ? (price - cost) / price : 0;

  const mlNet = useMemo(() => {
    const gross = Number(pvp || 0) * Math.max(1, Number(mlUnitsPerSale || 1));
    const commission = gross * (Number(mlCommissionPct || 0) / 100);
    const shipping = mlFreeShipping ? Number(mlShippingCost || 0) : 0;
    return Math.max(0, gross - commission - shipping);
  }, [pvp, mlUnitsPerSale, mlCommissionPct, mlFreeShipping, mlShippingCost]);

  const mlMarginBase = useMemo(() => pvp > 0 ? (mlNet - cost) / pvp : 0, [mlNet, cost, pvp]);
  const mlMarginWithAcos = useMemo(() => mlMarginBase - 0.12, [mlMarginBase]);

  const resetInputs = () => {
    setPvp(Number(selected?.price || 0));
    setPvpSim(Number(selected?.price || 0));
    setHoreca(0); setComercios(0); setDistribuidores(0);
    setMlCommissionPct(0); setMlFreeShipping(false); setMlShippingCost(0); setMlUnitsPerSale(1);
  };

  useEffect(() => { resetInputs(); }, [selected]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>Lista de precios</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Modificar el precio aquí solo afecta cotizaciones y ventas futuras. Las oportunidades y ventas ya creadas conservan el precio con el que fueron registradas.
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={products}
            getOptionLabel={(p: any) => p ? `${p.name}${p.sku ? ` (${p.sku})` : ''}` : ''}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            renderInput={(params) => <TextField {...params} label="Producto" placeholder="Buscar..." />}
          />
        </Grid>
      </Grid>

      {selected && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Costos</Typography>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField label="Cantidad (lote)" type="number" value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value)))} sx={{ width: { xs: '100%', sm: 200 } }} />
                    <Typography>Costo unitario (Q={qty}): <strong>${cost.toFixed(2)}</strong></Typography>
                  </Stack>
                  {costResp && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2">Desglose</Typography>
                      <Typography variant="body2">Materiales: ${Number(costResp.unitCostMaterials || 0).toFixed(2)} • Servicios: ${Number(costResp.unitCostServices || 0).toFixed(2)} • Total: ${Number(costResp.unitCostTotal || 0).toFixed(2)}</Typography>
                    </>
                  )}
                  {!costResp && <Typography variant="body2" color="text.secondary">Sin pricing detallado: usando costos base</Typography>}
                  <Typography>Horas de servicios: <strong>{serviceHours}</strong></Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Venta público</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="PVP" type="number" value={pvp} onChange={e => setPvp(Number(e.target.value))} fullWidth />
                  <TextField label="Margen" value={(margin(pvp)*100).toFixed(2) + '%'} InputProps={{ readOnly: true }} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                  <TextField label="PVP simulado" type="number" value={pvpSim} onChange={e => setPvpSim(Number(e.target.value))} fullWidth />
                  <TextField label="Margen simulado" value={(margin(pvpSim)*100).toFixed(2) + '%'} InputProps={{ readOnly: true }} fullWidth />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>HORECA / Comercios / Distribuidores</Typography>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="HORECA" type="number" value={horeca} onChange={e => setHoreca(Number(e.target.value))} fullWidth />
                    <TextField label="Margen HORECA" value={(margin(horeca)*100).toFixed(2) + '%'} InputProps={{ readOnly: true }} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Comercios" type="number" value={comercios} onChange={e => setComercios(Number(e.target.value))} fullWidth />
                    <TextField label="Margen Comercios" value={(margin(comercios)*100).toFixed(2) + '%'} InputProps={{ readOnly: true }} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Distribuidores" type="number" value={distribuidores} onChange={e => setDistribuidores(Number(e.target.value))} fullWidth />
                    <TextField label="Margen Distrib." value={(margin(distribuidores)*100).toFixed(2) + '%'} InputProps={{ readOnly: true }} fullWidth />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Mercado Libre</Typography>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Unidades x venta" type="number" value={mlUnitsPerSale} onChange={e => setMlUnitsPerSale(Number(e.target.value))} fullWidth />
                    <TextField label="PVP" type="number" value={pvp} onChange={e => setPvp(Number(e.target.value))} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Comisión ML %" type="number" value={mlCommissionPct} onChange={e => setMlCommissionPct(Number(e.target.value))} fullWidth />
                    <TextField label="¿Envío gratis? 0/1" type="number" value={mlFreeShipping ? 1 : 0} onChange={e => setMlFreeShipping(Number(e.target.value) === 1)} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Costo envío" type="number" value={mlShippingCost} onChange={e => setMlShippingCost(Number(e.target.value))} fullWidth />
                    <TextField label="Neto ML" value={mlNet.toFixed(2)} InputProps={{ readOnly: true }} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Margen ML base" value={(mlMarginBase*100).toFixed(2)+'%'} InputProps={{ readOnly: true }} fullWidth />
                    <TextField label="Margen ML - ACOS 12%" value={(mlMarginWithAcos*100).toFixed(2)+'%'} InputProps={{ readOnly: true }} fullWidth />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PricesPage;
